import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import META from '../seo/pageMeta.json';

/**
 * PageMeta — per-route <title>, meta description, canonical URL and the
 * Open Graph / Twitter equivalents for the public pages, so tabs, history and
 * in-browser shares read as the page rather than the generic site defaults.
 *
 * The route table lives in src/seo/pageMeta.json and is shared with
 * scripts/prerender-meta.js, which bakes the same values into a static
 * index.html per route at build time for link crawlers (WhatsApp, Facebook,
 * LinkedIn, X, search) that never run this JavaScript. Routes not in the
 * table keep the index.html defaults untouched.
 *
 * The canonical host is www.soulsathiya.com: the apex 301s there, so a
 * canonical on the apex would point search engines at a redirect.
 */
const { site, routes } = META;

// Set one attribute on the first <head> element matching `selector`; returns a
// function that restores the previous value (no-op if the tag is absent).
function setAttr(selector, attr, value) {
  const el = document.head.querySelector(selector);
  if (!el) return () => {};
  const prev = el.getAttribute(attr);
  el.setAttribute(attr, value);
  return () => { if (prev != null) el.setAttribute(attr, prev); };
}

export default function PageMeta() {
  const { pathname } = useLocation();

  useEffect(() => {
    const p = pathname.replace(/\/+$/, '') || '/';
    const entry = routes[p] || (p.startsWith('/blogs/') ? routes['/blogs'] : null);
    if (!entry) return undefined;

    const fullTitle = p === '/' ? entry.title : `${entry.title} · ${site.name}`;
    const url = site.origin + (p === '/' ? '/' : p);
    const image = `${site.origin}/og/${entry.image || site.image}.jpg`;

    const prevTitle = document.title;
    document.title = fullTitle;
    const restores = [
      setAttr('meta[name="description"]', 'content', entry.desc),
      setAttr('link[rel="canonical"]', 'href', url),
      setAttr('meta[property="og:url"]', 'content', url),
      setAttr('meta[property="og:title"]', 'content', fullTitle),
      setAttr('meta[property="og:description"]', 'content', entry.desc),
      setAttr('meta[property="og:image"]', 'content', image),
      setAttr('meta[name="twitter:url"]', 'content', url),
      setAttr('meta[name="twitter:title"]', 'content', fullTitle),
      setAttr('meta[name="twitter:description"]', 'content', entry.desc),
      setAttr('meta[name="twitter:image"]', 'content', image),
    ];
    return () => {
      document.title = prevTitle;
      restores.forEach((restore) => restore());
    };
  }, [pathname]);

  return null;
}
